import { useEffect, useRef, useState } from 'react';
import { Plus, X, Power, Building, UserCircle, UserCircle2, Mail, Tag, Search, Edit3, Trash2, Check, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, EyeOff, RefreshCw, Users as UsersIcon, ChevronUp, ChevronDown, Columns3, Loader2 } from 'lucide-react';
import { Modal } from "@mui/material";
import { motion } from 'framer-motion';
import { hasPermission } from '../utils/permissionsHelper';
import { jwt_decode } from '../utils/jwt_decode';
import baseapi from '../services/baseapi';
import { showError, showSuccess } from '../utils/toast';
import isEqual from 'lodash/isEqual';
import { useClientLevel } from '../hooks/useClientLevel';
import MultiSelectDropdown from '../components/MultiSelectDropdown';


interface User {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    enabled: boolean;
    attributes: {
        client_id?: (string | { id: number | string; value: string })[];
        tenant_name?: string[];
        role?: string[];
        organization_id?: string[];
    };
    client_id?: string | number;
    role: string | null;
}
interface Role {
    id: string;
    client_id: string;
    composite: boolean;
    description: string;
    is_deleted: boolean;
    organization_id: string;
    permissions: Record<string, string>;
    role: string;
}

interface SearchableDropdownProps {
    roles: Role[];
    value: string;
    onChange: (value: string) => void;
    error?: string;
    disabled?: boolean;
}

const SearchableRoleDropdown = ({ roles, value, onChange, error, disabled }: SearchableDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Filter roles based on search term
    const filteredRoles = roles.filter((role) =>
        role.role?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get selected role display name
    const selectedRole = roles.find((role) => role.role === value);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus input when dropdown opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleSelect = (roleName: string) => {
        onChange(roleName);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div>
            <label className={`block text-xs font-black uppercase tracking-widest mb-1.5 flex items-center gap-2 ${disabled ? 'text-slate-300' : 'text-slate-450 text-slate-400'}`}>
                <Tag size={14} className={disabled ? 'text-slate-300' : 'text-[#422568]'} />
                Role <span className="text-red-500 text-base">*</span>
            </label>

            <div className="relative" ref={dropdownRef}>
                {/* Dropdown trigger */}
                <button
                    type="button"
                    onClick={handleToggle}
                    disabled={disabled}
                    className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-sm flex items-center justify-between transition-all duration-300 ${error
                            ? 'border-red-500 bg-red-50/50 text-red-900'
                            : 'border-slate-100 text-slate-800'
                        } ${disabled
                            ? 'opacity-50 cursor-not-allowed bg-slate-100 border-slate-100 text-slate-400'
                            : 'hover:bg-white hover:border-slate-200'} ${isOpen ? 'border-[#422568] bg-white ring-4 ring-[#422568]/10' : ''}`}
                >
                    <span className={selectedRole && !disabled ? 'text-slate-800 font-bold' : 'text-slate-400 font-bold'}>
                        {selectedRole ? selectedRole.role : (disabled ? 'Select a client first' : 'Select a role')}
                    </span>
                    <ChevronDown
                        size={16}
                        className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#422568]' : ''}`}
                    />
                </button>

                {/* Dropdown menu */}
                {isOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl max-h-64 overflow-hidden origin-top animate-in fade-in-0 zoom-in-95 duration-100">
                        {/* Search input */}
                        <div className="p-2 border-b border-slate-100 bg-slate-50/50">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Search roles..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-sm text-slate-800 font-bold placeholder-slate-400 focus:border-[#422568] focus:outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* Options list */}
                        <div className="overflow-y-auto max-h-48 custom-scroll">
                            {filteredRoles.length > 0 ? (
                                filteredRoles.map((role) => (
                                    <button
                                        key={role.id}
                                        type="button"
                                        onClick={() => handleSelect(role.role)}
                                        className={`w-full px-4 py-2.5 text-left text-sm font-bold transition-all duration-150 ${value === role.role
                                                ? 'bg-[#422568] text-[#422568] font-black'
                                                : 'text-slate-600 hover:bg-slate-50'
                                            }`}
                                    >
                                        {role.role}
                                    </button>
                                ))
                            ) : (
                                <div className="px-4 py-8 text-center text-slate-400 text-[10px] font-black uppercase tracking-widest">
                                    No roles found
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Error message */}
            {error && (
                <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                    <AlertCircle size={12} />
                    {error}
                </div>
            )}
        </div>
    );
};


const Users = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [editingUser, setEditingUser] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [users, setUsers] = useState<User[]>([]);
    const [deleteUser, setDeleteUser] = useState<User | null>(null);
    const [originalFormData, setOriginalFormData] = useState<any>(null);
    const [open, setOpen] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);

    // eslint-disable-next-line
    const jwt = jwt_decode();
    const organization_id = jwt.organization_id;
    const tenant_name = jwt.tenant_id || jwt.tenant_name; // fallback if needed

    // Pagination states
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const isFirstSearch = useRef(true);

    //permissions
    const canCreate = hasPermission('users', 'create')
    const canUpdate = hasPermission('users', 'update')
    const canDelete = hasPermission('users', 'delete')

    // Column visibility state
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    type ColumnKey = 'first_name' | 'last_name' | 'email' | 'client' | 'role' | 'status' | 'actions';
    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        first_name: true,
        last_name: true,
        email: true,
        client: true,
        role: true,
        status: true,
        actions: canUpdate || canDelete
    });

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        enabled: true,
        role: ''
    });

    const level = useClientLevel();
    const [clients, setClients] = useState<{
        id: any; name: string; display_name: string
    }[]>([]);
    const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);

    useEffect(() => {
        if (level?.name === 'global') {
            baseapi.get('/clients', { params: { limit: 1000, skip: 0 } })
                .then(res => {
                    if (res.data.status) {
                        setClients(res.data.data);
                    }
                })
                .catch(() => { });
        }
    }, [level]);

    useEffect(() => {
        fetchData(0, itemsPerPage);
    }, [level]);

    // Fetch roles ONLY when clients change AND at least one client is selected
    useEffect(() => {
        if (level?.name === 'global') {
            if (selectedClientIds.length > 0) {
                fetchRoles();
            } else {
                setRoles([]); // Clear roles if no client selected
            }
        } else if (level?.name) {
            // At a specific tenant/client level, fetch directly
            fetchRoles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClientIds, level]);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    // Re-fetch when debouncedSearch changes
    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        fetchData(0, itemsPerPage);
    }, [debouncedSearch]);

    const fetchData = async (skip: number = 0, limit: number = 5, orderBy: Record<string, 'asc' | 'desc'>[] = [{ created: 'desc' }]) => {
        try {
            setLoading(true);
            const [sortEntry] = orderBy;
            const sort_by = Object.keys(sortEntry)[0];
            const sort_order = sortEntry[sort_by];

            let params: any = {
                skip: skip,
                limit: limit,
                sort_by,
                sort_order
            }

            if (debouncedSearch) {
                params.query = debouncedSearch;
            }
            if (level?.name !== 'global') {
                params.client_id = level?.id || '';
            }

            const response = await baseapi.get("/users", { params });
            if (response.data.status) {
                setUsers(response.data.data);
                setTotalItems(response.data.total);
                setTotalPages(Math.ceil(response.data.total / limit));
                const maxPage = Math.ceil(response.data.total / limit) || 1;
                if (currentPage > maxPage) {
                    setCurrentPage(maxPage);
                }
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'An error occurred';
            showError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        let params: any = {};

        // Check if we are at the global level
        if (level?.name === "global") {
            if (selectedClientIds.length === 1) {
                // Only one client selected: pass the specific client ID
                params = { query: JSON.stringify({ client_id: Number(selectedClientIds[0]) }) };
            } else if (selectedClientIds.length > 1) {
                // Multiple clients selected: pass is_global: true
                params = { query: JSON.stringify({ is_global: true }) };
            } else {
                return; // Guard: do nothing if 0 clients
            }
        } else {
            // Not global level: Use the current level's ID
            params = { query: JSON.stringify({ client_id: level?.id ? Number(level.id) : undefined }) };
        }

        try {
            const response = await baseapi.get("/roles", { params });
            if (response.data.status) {
                setRoles(response.data.data);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error?.response?.data?.message || error.message);
        }
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc';
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: newDirection }]);
                return { key, direction: newDirection };
            } else {
                // First interaction with a column - start with 'asc'
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: 'asc' }]);
                return { key, direction: 'asc' };
            }
        });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // Basic info validation
        if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
        if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
        if (!formData.role) newErrors.role = 'Role is required';
        if (level?.name === 'global' && selectedClientIds.length === 0) newErrors.client = 'Client is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleStatusChange = (enabled: boolean) => {
        setFormData(prev => ({ ...prev, enabled }));
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const clientIdToUse = level?.name === 'global' ? selectedClientIds : [level?.id];
            const params: any = {
                email: formData.email,
                first_name: formData.first_name,
                last_name: formData.last_name,
                enabled: formData.enabled,
                attributes: {
                    client_id: clientIdToUse,
                    organization_id: organization_id,
                    tenant_name: tenant_name,
                    role: formData.role
                },
                role: formData.role
            };

            if (level?.name === 'global') {
                params.client_ids = selectedClientIds;
            }

            let response;
            if (editingUser) {
                response = await baseapi.put(`/users/${editingUser}`, params);
            } else {
                response = await baseapi.post('/users', params);
            }

            if (response.data.status) {
                showSuccess(response.data.message);
                resetForm();
                setShowAddForm(false);
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'An error occurred';
            showError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            enabled: true,
            role: ''
        });
        setEditingUser(null);
        setShowAddForm(false);
        setErrors({});
        setSelectedClientIds([]);
    };


    const handleEdit = (user: User) => {
        const editData = {
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            enabled: user.enabled,
            role: user.role || user.attributes?.role?.[0] || ''
        };
        setFormData(editData);
        setOriginalFormData(editData);
        setEditingUser(user.id);
        setShowAddForm(true);
        // Set the client dropdown to the user's client (for global level)
        if (level?.name === 'global') {
            const clientAttr = user.attributes?.client_id || [];
            const cid = clientAttr.map(c => typeof c === 'object' ? String(c.id) : String(c)) || (user.client_id ? [String(user.client_id)] : []);
            setSelectedClientIds(cid);
        }
    };

    const isFormChanged = editingUser ?
        (!isEqual(formData, originalFormData) || !isEqual(selectedClientIds, originalFormData.client_id))
        : true;

    const handleDelete = async (userId: string) => {
        try {
            setLoading(true);

            // Optimistically update users
            const updatedUsers = users.filter(user => user.id !== userId);
            setUsers(updatedUsers);

            const response = await baseapi.delete(`/users/${userId}`);

            if (response.data.status) {
                showSuccess(response.data.message);
                // Fetch fresh data to ensure consistency
                await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
                // Adjust currentPage if necessary
                const newTotalPages = Math.ceil((totalItems - 1) / itemsPerPage) || 1;
                if (currentPage > newTotalPages) {
                    setCurrentPage(newTotalPages);
                }
            } else {
                showError(response.data.message || 'Failed to delete user');
                await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'An error occurred';
            showError(errorMsg);
            await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        setCurrentPage(1);
        setSearchTerm('');
        setDebouncedSearch('');
        fetchData();
        setTimeout(() => {
            setIsRefreshing(false);
        }, 1000);
    };

    // Pagination functions
    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchData((itemsPerPage * (page - 1)), itemsPerPage);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchData(0, value);
    };

    // Column visibility functions
    const handleColumnToggle = (column: ColumnKey) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: 'first_name', label: 'First Name' },
        { key: 'last_name', label: 'Last Name' },
        { key: 'email', label: 'Email' },
        { key: 'client', label: 'Clients' },
        { key: 'role', label: 'Role' },
        { key: 'status', label: 'Status' },
        ...(canUpdate || canDelete
            ? [{ key: 'actions' as ColumnKey, label: 'Actions' }]
            : []),
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 350, scale: 0 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
                duration: 0.6,
                ease: [0.25, 0.8, 0.25, 1], // Custom cubic-bezier for smoother flow
            }}
        >
            <div className="bg-slate-50 p-3 pb-20 min-h-screen">
                <div className="w-full mx-auto">
                    {/* Top Header Row */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-1.5">
                            <div className="bg-[#422568] p-1.5 rounded">
                                <UsersIcon className="w-4 h-4 text-white" />
                            </div>
                            <h1 className="font-black text-lg text-slate-800 leading-none flex items-center gap-1.5">
                                Users
                                {!loading && (
                                    <span className="text-slate-600 font-bold text-md">
                                        ({totalItems})
                                    </span>
                                )}
                            </h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                className="rounded-lg bg-white border border-slate-200 text-slate-400 p-2 hover:bg-slate-50 hover:text-slate-800 transition-all shadow-sm active:scale-95"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                            </button>

                            <div className="relative w-full sm:w-56">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-[10px] text-slate-800 focus:border-[#422568] focus:ring-4 focus:ring-[#422568]/5 outline-none transition-all font-bold placeholder:text-slate-400 shadow-sm"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>

                            <div className="relative" data-col-menu>
                                <button
                                    onClick={() => setShowColumnFilter(!showColumnFilter)}
                                    className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-800 text-slate-600 transition-all flex items-center gap-2 shadow-sm"
                                >
                                    <Columns3 className="w-3.5 h-3.5" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                                </button>
                                {showColumnFilter && (
                                    <div className="absolute top-full mt-2 right-0 w-52 bg-white border border-slate-200 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto origin-top animate-in fade-in-0 zoom-in-95 duration-100">
                                        <div className="px-3 py-1.5 text-[9px] font-bold text-slate-400 uppercase border-b border-slate-100 mb-1">Toggle Columns</div>
                                        {columnOptions.map((option) => (
                                            <button
                                                key={option.key}
                                                onClick={() => handleColumnToggle(option.key)}
                                                className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {visibleColumns[option.key] ? (
                                                        <Eye className="w-3 h-3 text-[#422568]" />
                                                    ) : (
                                                        <EyeOff className="w-3 h-3 text-slate-400" />
                                                    )}
                                                    {option.label}
                                                </div>
                                                <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${visibleColumns[option.key] ? 'bg-[#422568] border-[#422568]' : 'border-slate-300 bg-white'}`}>
                                                    {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {canCreate && (
                                <button
                                    onClick={() => {
                                        if (showAddForm) {
                                            resetForm();
                                        } else {
                                            setShowAddForm(true);
                                        }
                                    }}
                                    className="flex items-center gap-1.5 bg-[#422568] rounded-lg px-4 py-1.5 text-[10px] text-white font-bold hover:bg-[#422568] transition-all"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add User
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white border border-slate-200 rounded-t-xl overflow-hidden shadow-xl shadow-slate-200/50 mt-4">
                        <div className="overflow-x-auto custom-scroll">
                            <table className="w-full text-left text-xs border-collapse">
                                <thead className="bg-slate-50/80 sticky top-0 z-10 backdrop-blur-sm">
                                    <tr className="border-b border-slate-100">
                                        {columnOptions.map(({ key, label }) => (
                                            visibleColumns[key] && (
                                                <th
                                                    key={key}
                                                    onClick={() => key !== 'actions' && handleSort(key)}
                                                    className={`px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors ${key === 'actions' ? 'text-right' : 'text-left'}`}
                                                >
                                                    <div className={`flex items-center gap-1 ${key === 'actions' ? 'justify-end' : ''}`}>
                                                        {label}
                                                        {key !== 'actions' && (
                                                            sortConfig?.key === key ? (
                                                                sortConfig.direction === 'asc' ? (
                                                                    <ChevronUp size={12} className="text-[#422568]" />
                                                                ) : (
                                                                    <ChevronDown size={12} className="text-[#422568]" />
                                                                )
                                                            ) : (
                                                                <ChevronDown size={12} className="text-slate-300" />
                                                            )
                                                        )}
                                                    </div>
                                                </th>
                                            )
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-slate-400 font-bold uppercase tracking-widest italic animate-pulse">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Loader2 className="w-4 h-4 animate-spin text-[#422568]" />
                                                    Loading users
                                                </div>
                                            </td>
                                        </tr>
                                    ) : users.length === 0 ? (
                                        <tr>
                                            <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-slate-300 font-bold uppercase tracking-widest italic bg-slate-50/20">
                                                <div className="flex flex-col items-center gap-2">
                                                    <UsersIcon className="w-8 h-8 text-slate-100" /> No users found.
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        users.map((user) => (
                                            <tr key={user.id} className="hover:bg-slate-50 transition-colors group">
                                                {visibleColumns.first_name && (
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            
                                                            <span className="text-slate-800 text-[10px] font-bold leading-tight">{user.first_name}</span>
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.last_name && (
                                                    <td className="px-3 py-2 whitespace-nowrap"><span className="text-slate-800 text-[10px] font-bold leading-tight">{user.last_name}</span></td>
                                                )}
                                                {visibleColumns.email && (
                                                    <td className="px-3 py-2 whitespace-nowrap"><span className="text-slate-500 text-[10px] font-mono font-bold flex items-center gap-1.5 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">{user.email}</span></td>
                                                )}
                                                {visibleColumns.client && (
                                                    <td className="px-3 py-2 max-w-[150px]">
                                                        <div className="flex flex-wrap gap-1">
                                                            {user.attributes?.client_id && Array.isArray(user.attributes.client_id) && user.attributes.client_id.length > 0 ? (
                                                                user.attributes.client_id.map((c: any, i: number) => (
                                                                    <span key={i} className="text-[9px] bg-slate-100 text-[#422568] border border-[#422568] px-1.5 py-0.5 rounded truncate font-bold uppercase tracking-wide">
                                                                        {typeof c === 'object' ? (c.value || c.name) : c}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="text-slate-400 font-bold">-</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.role && (
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        {user.role || user.attributes?.role?.[0] ? (
                                                            <span className="text-[9px] font-mono bg-slate-100 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded uppercase font-bold">
                                                                {user.role || user.attributes?.role?.[0]}
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-400 font-mono font-bold">-</span>
                                                        )}
                                                    </td>
                                                )}

                                                {visibleColumns.status && (
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[9px] font-black uppercase border border-slate-200/60 bg-slate-50/50 shadow-sm">
                                                            <div className={`w-1.5 h-1.5 rounded-full ${user.enabled ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]'}`} />
                                                            <span className={`text-[9px] font-black uppercase tracking-wide ${user.enabled ? 'text-emerald-600' : 'text-rose-550 text-rose-500'}`}>{user.enabled ? 'Active' : 'Inactive'}</span>
                                                        </div>
                                                    </td>
                                                )}

                                                {visibleColumns.actions && (
                                                    <td className="px-3 py-2 whitespace-nowrap text-right">
                                                        <div className="flex items-center justify-end space-x-1.5">
                                                            {canUpdate && (
                                                                <button
                                                                    onClick={() => handleEdit(user)}
                                                                    className="text-slate-400 hover:text-slate-800 p-1.5 rounded-lg hover:bg-slate-100 transition-all active:scale-90"
                                                                >
                                                                    <Edit3 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}

                                                            {canDelete && (
                                                                <button
                                                                    onClick={() => {
                                                                        setDeleteUser(user);
                                                                        setOpen(true);
                                                                    }}
                                                                    className="text-rose-500 hover:text-rose-700 p-1.5 rounded-lg hover:bg-rose-50 transition-all active:scale-90"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Table Footer */}
                    <div className="bg-slate-50 border-t border-slate-200 px-6 py-2.5 flex items-center justify-between rounded-b-xl shadow-sm">
                        <div className="flex items-center gap-4">
                            <select
                                className="px-2 py-1 bg-white border border-slate-200 rounded text-slate-800 text-[10px] focus:outline-none focus:ring-1 focus:ring-[#422568] font-bold shadow-sm"
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                                <span className="text-slate-800">{startIndex + 1}</span> - <span className="text-slate-800">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"><ChevronsLeft className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"><ChevronLeft className="w-3.5 h-3.5" /></button>
                            <div className="px-2.5 py-1 bg-[#422568] rounded-md text-white text-[10px] font-black mx-1.5">{currentPage}</div>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"><ChevronRight className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"><ChevronsRight className="w-3.5 h-3.5" /></button>
                        </div>
                    </div>

                    {/* Sliding Add/Edit Form */}
                    <div
                        className={`fixed top-10 pb-25 right-0 sm:right-4 h-[calc(100vh-2rem)] w-full max-w-full sm:max-w-[480px] md:max-w-[520px] lg:max-w-[580px] transition-all duration-500 ease-in-out transform z-51 ${showAddForm ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                            }`}
                    >
                        <div className="h-full bg-white shadow-2xl border border-slate-200 rounded-2xl flex flex-col">
                            <div className="flex items-center justify-between px-4 py-3 sm:p-4 border-b border-slate-100 bg-slate-50/50 rounded-t-2xl">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 sm:p-3 bg-[#422568] rounded-xl">
                                        {editingUser ? <Edit3 className="text-white animate-pulse" size={20} /> : <Plus className="text-white animate-pulse" size={20} />}
                                    </div>
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-black text-slate-800">
                                            {editingUser ? 'Edit User' : 'Add New User'}
                                        </h2>
                                        <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mt-1">
                                            {editingUser ? 'Update user information' : 'Fill in the details to create a new user'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={resetForm}
                                    className="p-2 sm:p-3 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors active:scale-95"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Scrollable Form Content */}
                            <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scroll">
                                <div className="space-y-4">

                                    <div>
                                        <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-2">
                                            <UserCircle size={16} className="text-[#422568]" />
                                            First Name <span className="text-red-500 text-base">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.first_name}
                                            onChange={(e) => handleInputChange('first_name', e.target.value)}
                                            placeholder="Enter first name..."
                                            className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 text-sm font-semibold transition-all duration-300 ${errors.first_name
                                                    ? 'border-red-500 bg-red-50/50 text-red-900'
                                                    : 'border-slate-100 focus:border-[#422568] focus:bg-white focus:ring-4 focus:ring-[#422568]/10 outline-none'
                                                }`}
                                        />
                                        {errors.first_name && (
                                            <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                                                <AlertCircle size={12} />
                                                {errors.first_name}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-2">
                                            <UserCircle2 size={16} className="text-[#422568]" />
                                            Last Name <span className="text-red-500 text-base">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.last_name}
                                            onChange={(e) => handleInputChange('last_name', e.target.value)}
                                            placeholder="Enter last name..."
                                            className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 text-sm font-semibold transition-all duration-300 ${errors.last_name
                                                    ? 'border-red-500 bg-red-50/50 text-red-900'
                                                    : 'border-slate-100 focus:border-[#422568] focus:bg-white focus:ring-4 focus:ring-[#422568]/10 outline-none'
                                                }`}
                                        />
                                        {errors.last_name && (
                                            <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                                                <AlertCircle size={12} />
                                                {errors.last_name}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-2">
                                            <Mail size={16} className="text-[#422568]" />
                                            Email <span className="text-red-500 text-base">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            placeholder="Enter email..."
                                            className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 text-sm font-semibold transition-all duration-300 ${errors.email
                                                    ? 'border-red-500 bg-red-50/50 text-red-900'
                                                    : 'border-slate-100 focus:border-[#422568] focus:bg-white focus:ring-4 focus:ring-[#422568]/10 outline-none'
                                                }`}
                                        />
                                        {errors.email && (
                                            <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                                                <AlertCircle size={12} />
                                                {errors.email}
                                            </div>
                                        )}
                                    </div>

                                    {/* Client Dropdown - hidden at global level */}
                                    {level?.name === 'global' && (
                                        <div className="space-y-4">
                                            <MultiSelectDropdown
                                                label="Client"
                                                icon={Building}
                                                variant="project"
                                                options={clients.map(client => ({
                                                    label: client.display_name || client.name,
                                                    value: String(client.id),
                                                }))}
                                                value={selectedClientIds}
                                                onChange={(newClientIds) => {
                                                    setSelectedClientIds(newClientIds);
                                                    if (formData.role) {
                                                        // Reset the role explicitly when user alters the client selection
                                                        setFormData(prev => ({ ...prev, role: "" }));
                                                    }
                                                }}
                                                placeholder="Select clients"
                                                required
                                                error={errors.client}
                                                width="w-full"
                                            />
                                        </div>
                                    )}

                                    <SearchableRoleDropdown
                                        roles={roles}
                                        value={formData.role}
                                        onChange={(value) => handleInputChange('role', value)}
                                        error={errors.role}
                                        disabled={level?.name === 'global' && selectedClientIds.length === 0}
                                    />

                                    <div>
                                        <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2">
                                            <Power size={16} className="text-[#422568]" />
                                            Status
                                        </label>
                                        <div className="flex gap-3">
                                            <button
                                                type="button"
                                                onClick={() => handleStatusChange(true)}
                                                className={`inline-flex items-center justify-center px-5 py-2 text-xs font-bold rounded-xl transition-all shadow-sm ${formData.enabled
                                                    ? 'bg-emerald-600 text-white shadow-emerald-100 ring-4 ring-emerald-600/10'
                                                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                    }`}
                                            >
                                                Active
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleStatusChange(false)}
                                                className={`inline-flex items-center justify-center px-5 py-2 text-xs font-bold rounded-xl transition-all shadow-sm ${!formData.enabled
                                                    ? 'bg-rose-600 text-white shadow-rose-100 ring-4 ring-rose-600/10'
                                                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                    }`}
                                            >
                                                Inactive
                                            </button>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50/30">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting || (editingUser ? !isFormChanged : false)}
                                        className={`w-full sm:flex-1 flex items-center justify-center gap-3 bg-[#422568] text-white py-3 px-4 rounded-xl font-bold hover:bg-[#3c1e5f] transform hover:scale-102 active:scale-98 transition-all duration-200 shadow-md text-xs font-black uppercase tracking-wider
                                         ${(submitting || (editingUser && !isFormChanged)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting ? (
                                            <RefreshCw className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <Check size={18} />
                                        )}
                                        {submitting ? 'Submitting...' : (editingUser ? 'Update User' : 'Create User')}
                                    </button>

                                    <button
                                        onClick={resetForm}
                                        className="w-full sm:w-auto sm:px-6 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors duration-200 font-bold text-xs font-black uppercase tracking-wider"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Backdrop */}
                    <div
                        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 z-50 ${showAddForm ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                        onClick={resetForm}
                    />
                </div>

                {showColumnFilter && (
                    <div
                        className="fixed inset-0 z-30"
                        onClick={() => setShowColumnFilter(false)}
                    />
                )}

                <Modal open={open} onClose={() => setOpen(false)}>
                    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[500px] md:w-[550px] max-w-2xl bg-white 
                border border-slate-200 shadow-2xl p-4 rounded-2xl text-slate-800
                relative overflow-hidden">

                        {/* Subtle top border */}
                        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#422568]/40 to-transparent"></div>

                        {/* Header */}
                        <div className="flex items-center mb-4">
                            <div className="flex items-center justify-center mr-2 text-rose-500">
                                <Trash2 className='h-7 w-7'></Trash2>
                            </div>
                            <div className='flex items-center justify-center'>
                                <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Confirm Delete</h2>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="mb-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                            <p className="text-slate-800 mb-3 text-lg font-bold">
                                Are you sure you want to delete user <span className="font-extrabold text-[#422568]">{deleteUser?.first_name} {deleteUser?.last_name}</span>?
                            </p>
                            <p className="text-slate-455 text-slate-400 text-sm font-semibold">
                                This will permanently remove the user and all associated data from your account.
                            </p>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-4 justify-end">
                            <button
                                onClick={() => setOpen(false)}
                                className="px-6 py-3 border border-slate-200 text-slate-500 rounded-lg
                      hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800
                      transition-all duration-200 font-bold min-w-[100px]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    if (deleteUser) {
                                        handleDelete(deleteUser.id);
                                        setOpen(false);
                                    }
                                }}
                                className="px-6 py-3 bg-[#422568] text-white rounded-lg font-black uppercase tracking-wider
        hover:bg-[#422568] hover:shadow-lg hover:shadow-[#422568]
        transition-all duration-205 transition-all duration-200 min-w-[100px]"
                            >
                                Delete User
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </motion.div>
    );
};
export default Users;